/**
 * @file 主要存放一些页面的骨架
 */
import { Html, Head, Main, NextScript, DocumentProps } from 'next/document';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  MetaAndLink,
  CacheLocaleScript,
  SensorScript,
} from '@web3/base-components';
import { RTL_LIST, isLocal } from '@web3/base-utils';

interface Props {
  nextData: DocumentProps['__NEXT_DATA__'];
  monitorId: number;
  titleKey: string;
  keywordsKey: string;
  descriptionKey: string;
  twitterImgSrc?: string;
  tabIconSrc?: string;
  children?: React.ReactNode;
}

export const CommonDocument = (props: Props) => {
  const {
    nextData,
    monitorId,
    titleKey,
    keywordsKey,
    descriptionKey,
    twitterImgSrc,
    tabIconSrc,
    children,
  } = props;

  const pageProps = nextData.props?.pageProps || {};
  const messages = pageProps?.messages ?? {};
  const title = pageProps?.title;
  const keywords = pageProps?.keywords;
  const description = pageProps?.description;
  const locale = (nextData.query.locale ?? 'en') as string;

  return (
    <Html
      lang={locale}
      dir={RTL_LIST.includes(locale) ? 'rtl' : 'ltr'}
      className="moly-theme-dark"
    >
      <Head>
        {/* app内隐藏head */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                // 检查 User-Agent 中是否包含 'bybit_app'
                const isBybitApp = /bybit_app/.test(navigator.userAgent.toLowerCase());

                if (isBybitApp) {
                  // 创建一个新的 <style> 标签
                  const style = document.createElement('style');
                  style.innerHTML = '.web3-dangerous-header { display: none; }';
                  // 将 <style> 标签添加到 <head> 中
                  document.head.appendChild(style);
                }
              })();
            `,
          }}
        />
        <meta
          name="viewport"
          content="width=device-width; initial-scale=1; maximum-scale=1; minimum-scale=1; user-scalable=no;"
        />
        {/* 读取页面缓存语言 */}
        <CacheLocaleScript locale={locale} />
        {/* seo相关的meta和link信息 */}
        <MetaAndLink
          title={title || messages[titleKey]}
          keywords={keywords || messages[keywordsKey]}
          description={description || messages[descriptionKey]}
          locale={locale}
          imgSrc={twitterImgSrc}
          tabIconSrc={tabIconSrc}
        />
        {/* 埋点脚本 */}
        <SensorScript />
        {!isLocal && (
          <script
            src={`/common-static/infra-static/monitor/monitor.latest.js?id=${monitorId}`}
          ></script>
        )}
        {children}
      </Head>
      <body>
        <Main />
        <NextScript />
      </body>
    </Html>
  );
};
